/* eslint-disable max-len */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import moment from 'moment'
import { Button, Tabs, Tooltip } from '@enamelai/design-system'
import { Icon as IndentRightIcon } from '@enamelai/design-system/icons/Indent_right'
import './PatientProfile.css'
import {
  ScheduleAppointment,
  SelectContactModal,
} from '@components/layout/Modals'
import EmailSender from '@components/layout/Modals/EmailSender/EmailSender.component'
import { noteOverlayAppModalBootstrapProps } from '@components/common/NoteEditorOverlay'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import {
  setSelectedPatient,
  refreshFromPatient,
} from '@store/reducers/patientmanagement.reducer'
import { setActiveSidePanel } from '@store/reducers/uiSlice.reducer'
import { fetchPatientById } from '@services/api/patient.api'
import {
  AppointmentRequest,
  AppointmentResponse,
  PatientWithInsurance,
} from '@dataTypes/appointment.type'
import {
  createAppointment,
  getAppointmentsByPatientId,
} from '@services/api/appointment.api'
import { getFullName } from '@utils/fullname.util'
import { getPatientProfileReturnPath } from '@utils/patientProfileNavigation.util'
import useContact from '@components/pages/Messages/MessageCenterContact/useContact'
import { IContact } from '@dataTypes/contact.type'
import { mockPatients } from '@assets/mockdata/PatientTray/mockPatients'
import { mockFamilyMembers } from '@assets/mockdata/PatientTray/mockFamilyMembers'
import PatientProfileAccountSummary from '@pages/PatientProfile/AccountSummary/PatientProfileAccountSummary.component'
import PatientProfileInsuranceSummary from '@pages/PatientProfile/InsuranceSummary/PatientProfileInsuranceSummary.component'
import PatientProfileCommunicationsHistory from '@pages/PatientProfile/CommunicationsHistory/PatientProfileCommunicationsHistory.component'
import PatientProfileClinicalSummary from '@pages/PatientProfile/ClinicalSummary/PatientProfileClinicalSummary.component'
import PatientProfileProfileTab from '@pages/PatientProfile/ProfileTab/PatientProfileProfileTab.component'
import PatientProfileMembershipSummary from '@pages/PatientProfile/ProfileTab/PatientProfileMembershipSummary.component'
import PatientProfileAside from '@pages/PatientProfile/PatientProfileAside.component'
import PatientProfileForms from './PatientProfileForms.component'
import {
  buildPatientProfileMessagePreviousPage,
  navigateToMessageFromCommunicationHistory,
} from '@utils/patientProfileCommunicationNavigation.util'
import AppointmentSummary from './AppointmentSummary/AppointmentSummary.component'
import { useClinicProviders } from '@hooks/useClinicProviders'
import { useGlobalLoadingActions } from '@hooks/useGlobalLoading'
import { useClinicOperatories } from '@hooks/useClinicOperatories'
import { fetchAllAppointmentTypes } from '@store/actions'
import { v4 as uuidv4 } from 'uuid'
import {
  CommunicationItemType,
  ICommunicationHistory,
} from '@dataTypes/communicationHistory.type'

enum ProfileTabs {
  FORMS = 'forms',
  OVERVIEW = 'overview',
  APPOINTMENTS = 'appointments',
  CLINICAL = 'clinical',
  ACCOUNT = 'account',
  INSURANCE = 'insurance',
  COMMUNICATIONS = 'communications',
  MEMBERSHIP = 'membership',
}

const TAB_LABELS: { id: ProfileTabs; label: string }[] = [
  { id: ProfileTabs.OVERVIEW, label: 'Overview' },
  { id: ProfileTabs.APPOINTMENTS, label: 'Appointments' },
  { id: ProfileTabs.FORMS, label: 'Forms' },
  { id: ProfileTabs.CLINICAL, label: 'Clinical summary' },
  { id: ProfileTabs.ACCOUNT, label: 'Account summary' },
  { id: ProfileTabs.INSURANCE, label: 'Insurance summary' },
  { id: ProfileTabs.COMMUNICATIONS, label: 'Communications history' },
  { id: ProfileTabs.MEMBERSHIP, label: 'Membership summary' },
]

const PROFILE_TAB_ITEMS = TAB_LABELS.map((t) => ({
  id: t.id,
  label: t.label,
}))

/** Aside is hidden for now; set to true to show it again on non-Overview tabs. */
const SHOW_PROFILE_ASIDE = false

const PatientProfile: React.FC = () => {
  const { patientId = '' } = useParams<{ patientId: string }>()
  useClinicProviders()
  useClinicOperatories()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const contacts = useAppSelector((state) => state.contacts.data)
  const patient = useAppSelector((state) => state.patient.selectedPatient)
  const isHIPAAView = useAppSelector((state) => state.ui.isHIPAAView)
  const [activeTab, setActiveTab] = useState<ProfileTabs>(ProfileTabs.OVERVIEW)
  // TEMP: remove after empty-state UI QA
  const [forceEmptyStates, setForceEmptyStates] = useState(false)
  const [upcomingAppointmentsList, setUpcomingAppointmentsList] = useState<
    AppointmentResponse[]
  >([])
  const uuid = useRef<string>(uuidv4())
  const [loadError, setLoadError] = useState<string | null>(null)
  const [isSchedulingOpen, setIsSchedulingOpen] = useState(false)
  const { showLoading, hideLoading } = useGlobalLoadingActions()
  const [showEmailSender, setShowEmailSender] = useState(false)
  const location = useLocation()
  const { ...contactsHooks } = useContact()

  const handleCallClick = (): void => {
    if (!patient?.patient_id) return
    const patientContact = contactsHooks.filteredContacts.find(
      (contact) => contact.patient_id === patient.patient_id,
    )
    if (patientContact) {
      contactsHooks.handlePhoneAction(patientContact)
    }
  }

  const handleEmailClick = (): void => {
    setShowEmailSender(true)
  }

  const handleMessageClick = (): void => {
    if (!patient?.patient_id) return
    const patientContact = contactsHooks.filteredContacts.find(
      (c) => c.patient_id === patient.patient_id,
    )
    if (patientContact) {
      contactsHooks.handleSendMessage(patientContact)
      navigate('/message', {
        state: {
          previousPage: buildPatientProfileMessagePreviousPage(
            patient.patient_id,
            activeTab === ProfileTabs.COMMUNICATIONS
              ? 'communications'
              : 'profile',
          ),
        },
      })
    }
  }

  const handleCommunicationHistoryClick = useCallback(
    (item: ICommunicationHistory): void => {
      if (!patient?.patient_id) return

      const previousPage = buildPatientProfileMessagePreviousPage(
        patient.patient_id,
        activeTab === ProfileTabs.COMMUNICATIONS ? 'communications' : 'profile',
      )

      if (item.type === CommunicationItemType.SMS) {
        const patientContact = contactsHooks.filteredContacts.find(
          (contact) => contact.patient_id === patient.patient_id,
        )
        if (patientContact) {
          void contactsHooks.handleSendMessage(patientContact)
        }
      }

      navigateToMessageFromCommunicationHistory(navigate, item, previousPage)
    },
    [patient?.patient_id, activeTab, contactsHooks, navigate],
  )

  useEffect(() => {
    const profileTab = (
      location.state as { profileTab?: ProfileTabs } | null | undefined
    )?.profileTab
    if (
      profileTab &&
      Object.values(ProfileTabs).includes(profileTab as ProfileTabs)
    ) {
      setActiveTab(profileTab as ProfileTabs)
    }
  }, [location.state])

  useEffect(() => {
    setLoadError(null)
    if (!patientId) {
      setLoadError('Missing patient.')
      return
    }

    if (patientId.startsWith('mock-patient-')) {
      return
    }

    fetchPatientById(patientId, true)
      .then((res) => {
        if (res.statusCode !== 200 || !res.data?.patient) {
          setLoadError('Unable to load this patient.')
          return
        }
        const p = res.data.patient as PatientWithInsurance
        const contact = contacts?.find((c) => c.patient_id === p.patient_id)
        dispatch(setSelectedPatient({ patient: p as any, contact }))
      })
      .catch(() => {
        setLoadError('Unable to load this patient.')
      })
  }, [patientId, dispatch, contacts])

  useEffect(() => {
    setLoadError(null)
    if (!patientId.startsWith('mock-patient-')) return
    const mock = mockPatients.find((p) => p.patient_id === patientId)
    if (!mock) {
      setLoadError('Patient not found.')
      return
    }
    const contact = contacts?.find((c) => c.patient_id === mock.patient_id)
    dispatch(setSelectedPatient({ patient: mock, contact }))
  }, [patientId, dispatch, contacts])

  const fullName =
    patient && patient.patient_id === patientId
      ? getFullName(patient, false, isHIPAAView)
      : '—'

  const familyRows = useMemo(() => {
    if (!patientId) return []
    return mockFamilyMembers[patientId] ?? []
  }, [patientId])

  const showInsuranceSummaryDemo = patientId.startsWith('mock-patient-')
  const showCommunicationsHistoryDemo = patientId.startsWith('mock-patient-')
  const showClinicalSummaryDemo = patientId.startsWith('mock-patient-')
  const showAppointmentsDemo = patientId.startsWith('mock-patient-')
  const showAccountSummaryDemo = patientId.startsWith('mock-patient-')

  // Collapse ("Exit full profile") brings the patient side panel back. The
  // panel is opened on unmount so it runs after App has left the profile route
  // (App hides the panel while on /patient-profile/*).
  const reopenSidePanelOnExitRef = useRef(false)

  useEffect(
    () => () => {
      if (reopenSidePanelOnExitRef.current) {
        dispatch(setActiveSidePanel('main'))
      }
    },
    [dispatch],
  )

  const handleBack = (): void => {
    reopenSidePanelOnExitRef.current = true
    navigate(getPatientProfileReturnPath(location.state))
  }

  useEffect(() => {
    const fetchAppointmentTypes = async (): Promise<void> => {
      try {
        await dispatch(fetchAllAppointmentTypes()).unwrap()
      } catch {
        // non-blocking
      }
    }
    fetchAppointmentTypes()
  }, [dispatch])

  const fetchUpcomingAppointments = useCallback(async (): Promise<void> => {
    if (!patient) return
    const responseUpcoming = await getAppointmentsByPatientId(
      patient.patient_id,
      undefined,
      moment().format('YYYY-MM-DD'),
    )

    setUpcomingAppointmentsList(responseUpcoming?.data?.appointments || [])
  }, [patient])

  useEffect(() => {
    fetchUpcomingAppointments()
  }, [fetchUpcomingAppointments])

  const handleSaveAppointment = async (
    apptDetails: Partial<AppointmentRequest>,
  ): Promise<void> => {
    showLoading({
      container: '.patient-profile-page',
    })

    const response = await createAppointment(apptDetails)

    if (response.statusCode === 201) {
      setTimeout(() => {
        hideLoading('.patient-profile-page')
        dispatch(refreshFromPatient())
        fetchUpcomingAppointments()
        uuid.current = uuidv4()
      }, 2000)
    } else {
      setTimeout(() => {
        hideLoading('.patient-profile-page')
      }, 2000)
    }
  }

  if (loadError) {
    return (
      <div className="patient-profile-page patient-profile-error">
        <p className="patient-profile-error-text">{loadError}</p>
        <Button
          size="sm"
          onClick={() => navigate(getPatientProfileReturnPath(location.state))}
        >
          Go back
        </Button>
      </div>
    )
  }

  if (!patient || patient.patient_id !== patientId) {
    return (
      <div className="patient-profile-page patient-profile-loading">
        <span className="patient-profile-muted">Loading patient…</span>
      </div>
    )
  }

  return (
    <div
      className="patient-profile-page patient-profile-data-table-scope"
      data-node-id="28796:11539-root"
    >
      <header className="patient-profile-header">
        <nav
          className="patient-profile-tabs"
          aria-label="Patient profile sections"
        >
          <Tabs
            items={PROFILE_TAB_ITEMS}
            value={activeTab}
            onChange={(tabId) => setActiveTab(tabId as ProfileTabs)}
          />
        </nav>
        <div className="patient-profile-header-actions">
          {/* TEMP: remove after empty-state UI QA */}
          <Button
            variant="secondary"
            size="sm"
            className="patient-profile-empty-state-debug-btn"
            onClick={() => setForceEmptyStates((prev) => !prev)}
          >
            {forceEmptyStates ? 'Hide empty states' : 'Show empty states'}
          </Button>
          <Tooltip content="Exit full profile" placement="bottom" portal>
            <Button
              variant="ghost"
              size="sm"
              className="patient-profile-collapse"
              aria-label="Exit full profile"
              leftIcon={<IndentRightIcon aria-hidden="true" />}
              onClick={handleBack}
            />
          </Tooltip>
        </div>
      </header>

      <div
        className={`patient-profile-body${
          activeTab === ProfileTabs.OVERVIEW
            ? ' patient-profile-body--profile-tab'
            : ''
        }`}
      >
        <main className="patient-profile-main">
          {activeTab === ProfileTabs.OVERVIEW ? (
            <PatientProfileProfileTab
              patient={patient}
              fullName={fullName}
              isHIPAAView={isHIPAAView}
              scheduled={upcomingAppointmentsList}
              familyRows={familyRows}
              useDemoData={patientId.startsWith('mock-patient-')}
              forceEmptyStates={forceEmptyStates}
              onOpenAppointmentsTab={() =>
                setActiveTab(ProfileTabs.APPOINTMENTS)
              }
              onOpenCommunicationsTab={() =>
                setActiveTab(ProfileTabs.COMMUNICATIONS)
              }
              onCommunicationClick={handleCommunicationHistoryClick}
            />
          ) : activeTab === ProfileTabs.APPOINTMENTS ? (
            <AppointmentSummary
              key={uuid.current}
              patientId={patientId}
              useDemoData={showAppointmentsDemo}
              forceEmptyStates={forceEmptyStates}
            />
          ) : activeTab === ProfileTabs.FORMS ? (
            <PatientProfileForms patient={patient} />
          ) : activeTab === ProfileTabs.ACCOUNT ? (
            <PatientProfileAccountSummary
              useDemoData={showAccountSummaryDemo}
              forceEmptyStates={forceEmptyStates}
            />
          ) : activeTab === ProfileTabs.INSURANCE ? (
            <PatientProfileInsuranceSummary
              patientId={patientId}
              useDemoData={showInsuranceSummaryDemo}
              forceEmptyStates={forceEmptyStates}
            />
          ) : activeTab === ProfileTabs.COMMUNICATIONS ? (
            <PatientProfileCommunicationsHistory
              patientId={patientId}
              useDemoData={showCommunicationsHistoryDemo}
              onCommunicationClick={handleCommunicationHistoryClick}
              forceEmptyStates={forceEmptyStates}
            />
          ) : activeTab === ProfileTabs.MEMBERSHIP ? (
            <PatientProfileMembershipSummary
              patient={patient}
              fullName={fullName}
            />
          ) : activeTab === ProfileTabs.CLINICAL ? (
            <PatientProfileClinicalSummary
              patientId={patientId}
              useDemoData={showClinicalSummaryDemo}
              forceEmptyStates={forceEmptyStates}
            />
          ) : (
            <div className="patient-profile-placeholder">
              <p className="patient-profile-muted">
                This section is not available yet.
              </p>
            </div>
          )}
        </main>

        {SHOW_PROFILE_ASIDE && activeTab !== ProfileTabs.OVERVIEW ? (
          <PatientProfileAside
            patient={patient}
            fullName={fullName}
            isHIPAAView={isHIPAAView}
            onSchedule={() => setIsSchedulingOpen(true)}
            onCall={handleCallClick}
            onMessage={handleMessageClick}
            onEmail={handleEmailClick}
          />
        ) : null}
      </div>

      <EmailSender
        {...noteOverlayAppModalBootstrapProps}
        show={showEmailSender}
        onHide={() => setShowEmailSender(false)}
        recipientEmail={patient.email ?? ''}
      />
      {contactsHooks.showSelectContactModal && (
        <SelectContactModal
          {...noteOverlayAppModalBootstrapProps}
          show={contactsHooks.showSelectContactModal}
          onHide={contactsHooks.handleCloseSelectContactModal}
          contactData={contactsHooks.selectedContact as IContact}
        />
      )}

      {isSchedulingOpen && (
        <ScheduleAppointment
          {...noteOverlayAppModalBootstrapProps}
          modalId="schedule-appointment-modal-stacked"
          isOpen={isSchedulingOpen}
          handleModal={() => setIsSchedulingOpen(false)}
          onSave={handleSaveAppointment}
          initialStartTime={moment().format('YYYY-MM-DD')}
          initialEndTime={moment().format('YYYY-MM-DD')}
          initialRoom={undefined}
          initialProvider={undefined}
          currentAppt={undefined}
          {...(patient && {
            preSelectedPatient: {
              patient_id: patient.patient_id,
              first_name: patient.first_name,
              last_name: patient.last_name,
              has_insurance: patient.has_insurance,
              pat_ins: patient.pat_ins,
            } as PatientWithInsurance,
          })}
        />
      )}
    </div>
  )
}

export default PatientProfile
